import { LocaleIdentifier } from '@repo/markets/types'
import { imageQuality, urlForImage } from '@repo/sanity/image'
import { cn } from '@repo/ui/lib/utils'
import { createHrefForDocument } from '@repo/url/href'
import { PortableText as SanityPortableText, type PortableTextBlock, type PortableTextComponents } from 'next-sanity'
import { Image } from 'next-sanity/image'
import Link from 'next/link'
import { ElementType, FunctionComponent } from 'react'
import Prose from './Prose'

interface Props {
  locale: LocaleIdentifier
  value: PortableTextBlock[]
  className?: string
  as?: ElementType
  imageSizes?: string
  prose?: boolean
}

/**
 * This component uses Portable Text to render block content.
 *
 * You can learn more about Portable Text on:
 * https://www.sanity.io/docs/block-content
 * https://github.com/portabletext/react-portabletext
 * https://portabletext.org/
 */
const PortableText: FunctionComponent<Props> = ({ locale, className, value, as, imageSizes, prose = true }) => {
  const components: PortableTextComponents = {
    types: {
      richTextImage: ({ index, value }) => {
        const imageUrl = urlForImage(value)?.width(value?.asset?.metadata?.dimensions?.width)?.height(value?.asset?.metadata?.dimensions?.height)?.url() as string

        return (
          <figure>
            <Image
              src={imageUrl}
              sizes={imageSizes}
              quality={imageQuality}
              priority={index === 0}
              alt={value?.alt || value?.caption || ''}
              width={value?.asset?.metadata?.dimensions?.width}
              height={value?.asset?.metadata?.dimensions?.height}
              blurDataURL={value?.asset?.metadata?.lqip}
              placeholder={value?.asset?.metadata?.lqip ? 'blur' : undefined}
            />
            {value?.caption && <figcaption className='p-1 text-center text-sm'>{value.caption}</figcaption>}
          </figure>
        )
      },
      break: ({ value }) => {
        return <hr className={cn(value?.style === 'horizontalBreak' && 'bg-border h-px')} />
      },
    },
    marks: {
      richTextLink: ({ children, value }) => {
        if ((value?.type === 'manual' && value?.url) || (value?.type === 'email' && value?.email)) {
          return (
            <Link href={value?.type === 'email' ? `mailto:${value?.email}` : value?.url} target={value?.newTab ? '_blank' : undefined} rel='noreferrer noopener'>
              {children}
            </Link>
          )
        } else if (value?.type === 'internal' && value?.internalLink?.urlSlug?.current) {
          return (
            <Link
              title={value?.internalLink?.title}
              target={value?.newTab ? '_blank' : undefined}
              rel={value?.newTab ? 'noopener noreferrer' : undefined}
              href={createHrefForDocument(value?.internalLink?._type, value?.internalLink?.urlSlug?.current, locale) || ''}
            >
              {children}
            </Link>
          )
        }

        return <>{children}</>
      },
    },
  }

  const content = <SanityPortableText components={components} value={value} onMissingComponent={process.env.NODE_ENV === 'production' ? false : undefined} />

  return prose ? (
    <Prose className={className} as={as}>
      {content}
    </Prose>
  ) : (
    content
  )
}

export default PortableText
